import { defineStore } from "pinia";
import moment, { Moment } from 'moment';

import { Location } from "~/api/models/location.model";
import { useStationStore } from '~/store/station.store';
import { Measure } from "~/api/models/measure.model";
import { Pollution } from "~/api/models/pollution.model";
import { StationStats } from "~/api/models/station-stats.model";
import { Station } from "~/api/models/station.model";
import { Weather } from "~/api/models/weather.model";
import { StationIconeResponse } from "~/api/models/icone-response.model";


export const useDashboardStore = defineStore('dashboard', {
    state: () => ({
        currentStation: null as Station | null,
        stats: null as StationStats,
        indicator: 'CO2' as string | null,
        averages: [] as Measure[],
        mode: 'day' as 'hour' |'day' | 'week' | 'month',
        measures: [] as Measure[],
        icone: null as StationIconeResponse,
        weather: null as Weather | null,
        pollution: null as Pollution | null,
    }),

    getters: {
        city(): string | null {
            return this.currentStation?.metadata['elichens/public/locality'] as string || null;
        },

        temperatureExt(): number | null {
            return this.weather?.current?.temp ? Math.round(this.weather?.current?.temp) : null;
        },

        location(): Location | null {

            if(!this.currentStation || !this.currentStation.location.lat || !this.currentStation.location.lon) {
                return null;
            }

            return this.currentStation?.location || null;
        },

        aqiExt(): number {
            return this.pollution?.global_aqi?.elichens.value || null;
        },

        shouldStartOf(): moment.unitOfTime.StartOf {
            switch(this.mode) {
                case 'month':
                case 'week':
                    return 'day'; // nb minutes in a week divide by number of step we want
                    
                case 'hour':
                case 'day':
                default:
                    return 'hour';
            }
        },

        since(): Moment{
            switch(this.mode) {
                case 'month':
                case 'week':
                case 'day':
                    return this.before.clone().subtract(1, this.mode);
                case 'hour':
                    return this.before.clone().subtract(6, 'hour');
                default:
                    return this.before.clone().subtract(1, this.mode);
            }
            
        },

        before(): Moment {
            return moment().startOf(this.shouldStartOf);
        },

        step(): number {
            switch(this.mode) {
                case 'month':
                    return 6 * 60 * 60 ;

                case 'week':
                    return 120 * 60 // nb minutes in a week divide by number of step we want
                
                case 'day':
                    return 15 * 60;
                case 'hour':
                    return 60;
                default:
                    return 60;
            }
        },

    },

    actions: {
        async selectStation(stationId?: string, refresh: boolean = true): Promise<void> {
            const stationStore = useStationStore();
            const stations = stationStore.allStations;

            if(!stations?.length) {
                return;
            }
            
            const id = stationId ? stationId : stations[0].id;

            try {
                const station: Station = stations.find((station: Station) => station.id === id);
                if(!station) {
                    return;
                }
                   
                this.currentStation = station;

                if(refresh) {
                    await this.refresh();
                }
            } catch(e) {
                this.currentStation = null;
                this.stats = null;
                this.averages = null;
                this.pollution = null;
                this.weather = null;
                throw e;
            }
        },

        async refresh(): Promise<void> {
            
            if(!this.currentStation) {
                return Promise.reject(new Error('Could not refresh. No station selected'));
            }

            useStationStore().refresh();
            
            const [stats, averages, icone, weather, pollution] = await Promise.all([
                this.$stationService.getStationStats(this.currentStation.id, this.since, this.before).catch(),
                this.$stationService.getAverage(this.currentStation.id, this.step, this.since, this.before).catch(),
                this.$stationService.getStationICONE(this.currentStation.id).catch(),
                this.$weatherService.getWeather(this.location).catch(),
                this.$weatherService.getCurrentPollution(this.location).then((pollution: Pollution) => this.pollution = pollution ).catch(),
            ]);
            
            this.stats = stats;
            this.averages = averages;
            this.icone = icone;
            this.weather = weather;
            this.pollution = pollution;
        },

        async fetchMeasures(since?: Moment, before?: Moment, store: boolean = true): Promise<Measure[]>  {
            
            if(!this.currentStation) {
                return Promise.reject(new Error(`Could not fetch measures for current station. Current station is null or undefined`));
            }
            
            if(!since) {
                since = this.since;
            }
            
            if(!before) {
                before = this.before;
            }
            
            return this.$stationService.getMeasures(this.currentStation.id, since, before).then((measures: Measure[]) => {
                if(store) {
                    this.measures = measures;
                }
                
                return measures;
            });
        },
  
        async fetchWeather(): Promise<[Weather, Pollution]> {
            
            if(!this.location) {
                this.weather = null;
                this.pollution = null;
                return Promise.resolve([null, null]);
            }
            
            return Promise.all([
                this.$weatherService.getWeather(this.location),
                this.$weatherService.getCurrentPollution(this.location),
            ]).then(([weather, pollution]: [Weather, Pollution]) => {
                this.weather = weather;
                this.pollution = pollution;
                return [weather, pollution];
            });
        }
    }
});